import {mergeAttributes, Node} from "@tiptap/core";
import {ReactNodeViewRenderer} from "@tiptap/react";
import PrintoutImageNodeView from "./PrintoutImageNodeView";
import {
    clampPrintoutImageHeightMm,
    clampPrintoutImageWidthMm,
    DEFAULT_PRINTOUT_IMAGE_HEIGHT_MM,
    DEFAULT_PRINTOUT_IMAGE_WIDTH_MM,
    getPrintoutImageAttrs,
    parsePrintoutImageAlignment,
    parsePrintoutImageFit,
} from "./printoutImageOptions";
import type {PrintoutImageNodeAttrs} from "./printoutImageOptions";

export const PrintoutImageNode = Node.create({
    name: "printoutImage",

    group: "block",
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
        return {
            src: {
                default: null,
                parseHTML: element => element.getAttribute("data-printout-image-src"),
                renderHTML: attributes => attributes.src
                    ? {"data-printout-image-src": attributes.src}
                    : {},
            },
            publicId: {
                default: null,
                parseHTML: element => element.getAttribute("data-printout-image-public-id"),
                renderHTML: attributes => attributes.publicId
                    ? {"data-printout-image-public-id": attributes.publicId}
                    : {},
            },
            widthMm: {
                default: DEFAULT_PRINTOUT_IMAGE_WIDTH_MM,
                parseHTML: element => clampPrintoutImageWidthMm(element.getAttribute("data-printout-image-width-mm")),
                renderHTML: attributes => ({
                    "data-printout-image-width-mm": clampPrintoutImageWidthMm(attributes.widthMm),
                }),
            },
            heightMm: {
                default: DEFAULT_PRINTOUT_IMAGE_HEIGHT_MM,
                parseHTML: element => clampPrintoutImageHeightMm(element.getAttribute("data-printout-image-height-mm")),
                renderHTML: attributes => ({
                    "data-printout-image-height-mm": clampPrintoutImageHeightMm(attributes.heightMm),
                }),
            },
            alignment: {
                default: "center",
                parseHTML: element => parsePrintoutImageAlignment(element.getAttribute("data-printout-image-alignment")),
                renderHTML: attributes => ({
                    "data-printout-image-alignment": parsePrintoutImageAlignment(attributes.alignment),
                }),
            },
            fit: {
                default: "contain",
                parseHTML: element => parsePrintoutImageFit(element.getAttribute("data-printout-image-fit")),
                renderHTML: attributes => ({
                    "data-printout-image-fit": parsePrintoutImageFit(attributes.fit),
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="printout-image"]',
            },
        ];
    },

    renderHTML({HTMLAttributes, node}) {
        const attrs = getPrintoutImageAttrs(node.attrs as PrintoutImageNodeAttrs);
        const frameStyle = `width:${attrs.widthMm}mm;height:${attrs.heightMm}mm`;

        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-image",
                class: "printout-image-block",
            }),
            [
                "div",
                {class: "printout-image-frame", style: frameStyle},
                ...(attrs.src
                    ? [["img", {alt: "", src: attrs.src, style: `object-fit:${attrs.fit}`}]]
                    : []),
            ],
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutImageNodeView, {
            trackNodeViewPosition: true,
        });
    },
});
